// Constantes partagées entre client et serveur

// Modes de jeu avec détails et restrictions premium
export const GAME_MODES = {
  classic: {
    id: 'classic',
    name: 'Classique',
    description: 'Devinez le titre et l\'artiste. Le mode par défaut accessible à tous.',
    longDescription: 'Le mode classique du blind test ! Écoutez un extrait musical et tentez de deviner le titre et l\'artiste. Chaque bonne réponse rapporte des points, avec des bonus pour les réponses rapides.',
    icon: '🎵',
    color: 'from-blue-500 to-cyan-500',
    isPremium: false,
    features: ['Deviner titre et artiste', 'Bonus de rapidité', 'Jusqu\'à 100 joueurs'],
  },
  teams: {
    id: 'teams',
    name: 'Équipes',
    description: 'Affrontez-vous en équipes de 2 à 4.',
    longDescription: 'Formez des équipes et combattez ensemble ! Les points de chaque membre s\'additionnent au score de l\'équipe. Idéal pour les soirées entre amis.',
    icon: '👥',
    color: 'from-green-500 to-emerald-500',
    isPremium: true,
    features: ['Équipes de 2-4 joueurs', 'Score cumulé par équipe', 'Chat d\'équipe'],
  },
  'battle-royale': {
    id: 'battle-royale',
    name: 'Battle Royale',
    description: 'Élimination progressive. Le dernier survivant gagne !',
    longDescription: 'Mode élimination ! À chaque manche, le ou les joueurs avec le moins de points sont éliminés. Survivez jusqu\'à la fin pour remporter la victoire.',
    icon: '🏆',
    color: 'from-red-500 to-pink-500',
    isPremium: true,
    features: ['Éliminations progressives', 'Tension croissante', 'Dernier survivant gagne'],
  },
  quiz: {
    id: 'quiz',
    name: 'Quiz',
    description: 'Choix multiples parmi 4 propositions.',
    longDescription: 'Format quiz classique ! 4 propositions s\'affichent et vous devez choisir la bonne réponse. Plus accessible mais tout aussi fun !',
    icon: '❓',
    color: 'from-indigo-500 to-blue-500',
    isPremium: true,
    features: ['4 propositions', 'Plus accessible', 'Format quiz TV'],
  },
  deaf: {
    id: 'deaf',
    name: 'Deaf Test',
    description: 'Devinez la musique à partir du clip vidéo muet !',
    longDescription: 'Le clip vidéo tourne sans son ! Devinez le titre et l\'artiste uniquement à partir des images. À la fin de chaque round, le son est révélé pendant 5 secondes.',
    icon: '🎬',
    color: 'from-pink-500 to-red-500',
    isPremium: true,
    features: ['Clip vidéo muet', 'Points x2', 'Révélation audio'],
  },
} as const;

// Types pour les modes de jeu
export type GameModeId = keyof typeof GAME_MODES;
export type GameModeInfo = typeof GAME_MODES[GameModeId];

// Power-ups
export const POWER_UPS = {
  '50-50': {
    id: '50-50',
    name: '50/50',
    description: 'Révèle la moitié du titre',
    icon: '🎯',
    cost: 50,
  },
  skip: {
    id: 'skip',
    name: 'Passer',
    description: 'Passer cette chanson',
    icon: '⏭️',
    cost: 30,
  },
  hint: {
    id: 'hint',
    name: 'Indice',
    description: 'Première lettre ou année',
    icon: '💡',
    cost: 40,
  },
  steal: {
    id: 'steal',
    name: 'Voler',
    description: 'Voler 20 points à un joueur',
    icon: '💰',
    cost: 100,
  },
  replay: {
    id: 'replay',
    name: 'Rejouer',
    description: 'Réécouter l\'extrait',
    icon: '🔄',
    cost: 20,
  },
} as const;

// Limites
export const LIMITS = {
  ROOM_NAME_MAX: 50,
  PLAYER_NAME_MAX: 20,
  MESSAGE_MAX: 500,
  ROOM_MAX_PLAYERS: 100,
  ROUND_MIN: 5,
  ROUND_MAX: 50,
  ROUND_DURATION_MIN: 10,
  ROUND_DURATION_MAX: 60,
} as const;

// Scoring
export const SCORING = {
  BASE_TITLE: 100,
  BASE_ARTIST: 80,
  SPEED_BONUS_MAX: 0.5, // 50% bonus max
  COMBO_BONUS_PER_STREAK: 2,
  COMBO_BONUS_MAX: 10,
  RANK_BONUS_1ST: 50,
  RANK_BONUS_2ND: 30,
  RANK_BONUS_3RD: 10,
} as const;

// XP et Niveaux
export const XP_PER_LEVEL = 1000;
export const XP_REWARDS = {
  GAME_PLAYED: 10,
  GAME_WON: 50,
  CORRECT_TITLE: 5,
  CORRECT_ARTIST: 5,
  PERFECT_ROUND: 20,
  WIN_STREAK_3: 30,
  WIN_STREAK_5: 50,
  WIN_STREAK_10: 100,
} as const;

// Timings (ms)
export const TIMINGS = {
  GAME_START_DELAY: 3000,
  BETWEEN_ROUNDS_DELAY: 5000,
  GAME_END_DELAY: 5000,
  PING_INTERVAL: 25000,
  PING_TIMEOUT: 60000,
} as const;

// Couleurs du thème
export const THEME_COLORS = {
  primary: '#6366f1',
  secondary: '#8b5cf6',
  success: '#22c55e',
  warning: '#f59e0b',
  error: '#ef4444',
  background: '#0f0f1a',
  surface: '#1a1a2e',
  text: '#ffffff',
  textMuted: '#94a3b8',
} as const;
